import NewLayout from "@components/new-ui/Layout";
import { windowOpenNewTab } from "@utils/common";
import "keen-slider/keen-slider.min.css";
import Image from "next/image";
import { FC } from "react";
import { MdOutlineArrowForwardIos } from "react-icons/md";
import AwardsSection from "@components/new-ui/AwardsSection";
import CTASection from "@components/new-ui/CTASection";
import CaseStudyFeaturesSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos, { LogoObject } from "@components/new-ui/ClientLogos";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import StatsInfo, { StatsInfoProps } from "@components/new-ui/IndustriesSection/StatsInfo";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import { container_classname } from "@components/new-ui/constants";

interface RealEstateProps {
    data: any;
}

const herosectionData: HerosectionProps = {
    chipTitle: "REAL ESTATE",
    title: "WhatsApp for Real Estate — Sell More, Follow Up Faster",
    description:
        "From sharing new property launches to qualifying leads to booking property visits, Gallabox’s broadcast feature, no-code AI chatbots and shared team inbox make it a sweet deal.",
    primaryButtonText: "Try for Free",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/wa-chatbot-real-estate.png",
};

const statsInfoData: Array<StatsInfoProps> = [
    { percentage: "34%", label: "Reduction in lead follow-up time" },
    { percentage: "53%", label: "Reduction in lead qualification costs" },
    { percentage: "71%", label: "Increase in conversion rates" },
];

const featureInfo1: FeatureInfo = {
    mainTitle: "Drive real estate campaigns",
    subtitle: "By increasing reach with engaging broadcasts",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/real-estate-templates.png",
    featureInfoList: [
        "Open house events’ promotion",
        "Sharing of exclusive offers/discounts",
        "Running surveys to gauge buyer interest",
        "Promotion of community-building initiatives",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Capture property leads effortlessly",
    subtitle: "Using smart automation capabilities",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/ask-button-cards.png",
    featureInfoList: [
        "Bring Meta ad leads to chat on WhatsApp",
        "Instant property match suggestions",
        "Automated budget qualification process",
        "24/7 property information assistance",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Streamline property viewings ",
    subtitle: "By leveraging hassle-free scheduling",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/free-scheduling.png",
    featureInfoList: ["Automated viewing appointments", "Real-time agent coordination", "Virtual tour scheduling service", "Instant feedback collection system"],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Enhance buyer engagement",
    subtitle: "With relevant real-time responses",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/real-time-responses.png",
    featureInfoList: [
        "New listing and deal notifications",
        "Personalized property recommendations",
        "Delivery of neighborhood insights",
        "Sharing of brochures, videos, and catalogs",
    ],
};
const featureInfo5: FeatureInfo = {
    mainTitle: "Accelerate deal closure",
    subtitle: "By deploying seamless processes",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/seamless-processes.png",
    featureInfoList: ["Automated document collection system", "Payment schedule notifications", "Legal requirement updates", "Mortgage option recommendations"],
};

const productCardData: Array<Product> = [
    {
        title: "Click-to-WhatsApp ads",
        description: "Capture interest from Meta ads and instantly communicate property details on WhatsApp.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/click-whatsapp-ads.png",
        externalLink: "https://gallabox.com/click-to-whatsapp-ads",
    },
    {
        title: "Broadcast",
        description: "Share property launches, spaces for sales, and lifestyle newsletters.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/broadcast.png",
        externalLink: "https://gallabox.com/whatsapp-broadcast",
    },
    {
        title: "Drip marketing",
        description: "Send timely reminders and nudges that push for property viewing appointments.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/drip-marketing.png",
        externalLink: "https://gallabox.com/whatsapp-drip-marketing",
    },
    {
        title: "Flows",
        description: "Collect buyer preferences for providing a personalized property evaluation experience.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/whatsapp-flow.png",
        externalLink: "https://gallabox.com/whatsapp-flows",
    },
    {
        title: "Chatbot",
        description: "Answer FAQs, qualify leads, and help book or reschedule property viewings and consultations.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/bot.png",
        externalLink: "https://gallabox.com/whatsapp-chatbot",
    },
    {
        title: "Shared team inbox",
        description: "Respond and close deals without any delays using an AI-powered shared inbox.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/inbox.png",
        externalLink: "https://gallabox.com/whatsapp-shared-inbox",
    },
    {
        title: "AI rewrite",
        description: "Edit your responses with our built-in Gen AI and send the most professional property proposals.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/gallabox-gen-ai.png",
        externalLink: "https://gallabox.com/whatsapp-ai-chatbot",
    },
    {
        title: "Payments",
        description: "Securely request and receive payments for documentation and services with detailed billing info.",
        imageUrl: "https://assets.gallabox.com/gb-home/industry-logos/education/group.png",
        externalLink: "https://gallabox.com/whatsapp-payments",
    },
];

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/industries/real-estate/footprint-estate.png",
        chiptitle: "REAL ESTATE",
        title: "Gallabox revolutionizes lead engagement for Footprint Real Estate",
        statsInfoData: [
            { percentage: "150%", label: "Growth in customer conversion" },
            { percentage: "100%", label: "Increase in lead generation" },
            { percentage: "100%", label: "Expansion in customer base" },
        ],
        url: "https://gallabox.com/case-study/footprint-real-estate",
    },
];

const clientLogosData: LogoObject[] = [
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/adeer.png", alt: "adeer", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/credai.png", alt: "credai", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/estative.png", alt: "estative", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/housiey.png", alt: "housiey", width: 160, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/inspira.png", alt: "inspira", width: 160, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/lansum.png", alt: "lansum", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/luxprop.png", alt: "luxprop", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/m3m.png", alt: "m3m", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/nationwide.png", alt: "nationwide", width: 180, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/propleaf.png", alt: "propleaf", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/range.png", alt: "range", width: 160, height: 40 },
];

const RealEstate: FC<RealEstateProps> = (props) => {
    const { data } = props;

    return (
        <div style={{ backgroundColor: "#fff" }}>
            <NewLayout
                title="WhatsApp for Real Estate — Lead Capture, Follow-Up & Virtual Tours"
                description="Close more property deals with WhatsApp. Automate lead follow-up, send virtual tour links & schedule site visits. Free for agents."
                ogAttributes={{ image: "https://assets.gallabox.com/gb-home/industries/real-estate/gallabox_industries_real+estate-og.png" }}
                canonical="https://gallabox.com/industry/real-estate/whatsapp"
            >
                <Herosection herosection={herosectionData} />

                <StatsInfo statsInfo={statsInfoData} />

                <section className={`${container_classname} my-10`}>
                    <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                    <ClientLogos logos={clientLogosData} />
                </section>

                <FeatureInfoCard {...featureInfo1} />
                <FeatureInfoCard {...featureInfo2} />
                <FeatureInfoCard {...featureInfo3} />
                <FeatureInfoCard {...featureInfo4} />
                <FeatureInfoCard {...featureInfo5} />

                <ProductSection
                    title="The only bridge you need to connect dreams with reality"
                    description="Cement your prospect relationships with Gallabox’s user-friendly features"
                    productCardData={productCardData}
                />

                <section className={`${container_classname} py-20`}>
                    <h2 className="md:text-6xl text-4xl mb-12" style={{ fontFamily: "Degular Display" }}>
                        Ready-to-test education chatbot templates
                    </h2>
                    <div className={"grid md:grid-cols-3 md:gap-5 gap-10"}>
                        {data.slice(0, 3).map((data: any) => (
                            <div
                                key={data.id}
                                className="border border-solid border-gray-200 rounded cursor-pointer"
                                onClick={() => windowOpenNewTab(`/whatsapp-chatbot-templates/${data.category}/${data.title}/${data.id}`)}
                            >
                                <div className="relative">
                                    <img src={data.picture} alt={data.title} width={280} height={170} style={{ height: "200px" }} className="w-full" />

                                    <div
                                        className="absolute top-0 left-0 w-full h-full"
                                        style={{
                                            background: "linear-gradient(180deg, rgba(51, 100, 250, 0.00) 0%, rgba(51, 100, 250, 0.12) 100%)",
                                        }}
                                    ></div>
                                </div>
                                <div className="p-8 bg-white">
                                    {data.tags?.map((tag: { name: string }) => (
                                        <p key={tag.name} className="mb-4 text-gray-700 text-sm">
                                            {tag.name.toUpperCase()}
                                        </p>
                                    ))}
                                    <p className="mb-12 text-2xl font-medium">{data.title}</p>
                                    <div className="flex gap-x-4 items-center">
                                        <a
                                            href={`/try-template/${data.id}`}
                                            className="border border-solid border-blue-600 text-blue-600 rounded no-underline hover:no-underline text-sm"
                                            style={{ lineHeight: "14px", padding: "8px 12px 8px 12px" }}
                                        >
                                            TRY
                                        </a>
                                        <div className="flex gap-1 items-center">
                                            <Image src="/new-industries/education/view.svg" width={20} height={20} className="w-8 h-6" alt="visited" />
                                            {data.usedCount}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-9 text-blue-500 flex justify-center">
                        <a href="https://gallabox.com/whatsapp-chatbot-templates/education" className="flex items-center gap-x-2">
                            View all <MdOutlineArrowForwardIos size={14} />
                        </a>
                    </p>
                </section>
                <CaseStudyFeaturesSection features={caseStudyFeatureCardData} />

                <IntegrationSection isLight={true} />
                <AwardsSection />
                <CTASection
                    title="Real estate can have long sales cycles. Cut the wait by being proactive sellers on WhatsApp."
                    description="Real estate can have long sales cycles. Cut the wait by being proactive sellers on WhatsApp."
                />
            </NewLayout>
        </div>
    );
};

export default RealEstate;
